import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import ProjectCard from "@/components/ProjectCard";
import { AREAS } from "@/lib/constants";
import { Search, Archive, Sparkles, Pause } from "lucide-react";

const STATUS_TABS = [
  { key: "attivi", label: "Progetti attivi", icon: Sparkles, filter: (p) => p.status === "attivo" },
  { key: "in_pausa", label: "In pausa", icon: Pause, filter: (p) => p.status === "in_pausa" },
  { key: "conclusi", label: "Archivio (conclusi)", icon: Archive, filter: (p) => p.status === "concluso" },
  { key: "tutti", label: "Tutti", icon: null, filter: () => true },
];

export default function Progetti() {
  const [params, setParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [q, setQ] = useState(params.get("q") || "");
  const [area, setArea] = useState(params.get("area") || "");
  const [tab, setTab] = useState(params.get("stato") || "attivi");

  useEffect(() => {
    const p = new URLSearchParams();
    if (area) p.set("area", area);
    if (q) p.set("q", q);
    api.get(`/projects?${p.toString()}`).then((r) => setProjects(r.data));
    const url = new URLSearchParams();
    if (area) url.set("area", area);
    if (q) url.set("q", q);
    if (tab && tab !== "attivi") url.set("stato", tab);
    setParams(url);
  }, [q, area, tab]);

  const counts = useMemo(() => ({
    attivi: projects.filter((p) => p.status === "attivo").length,
    in_pausa: projects.filter((p) => p.status === "in_pausa").length,
    conclusi: projects.filter((p) => p.status === "concluso").length,
    tutti: projects.length,
  }), [projects]);

  const activeTab = STATUS_TABS.find((t) => t.key === tab) || STATUS_TABS[0];
  const filtered = projects.filter(activeTab.filter);
  const showActiveThenArchive = tab === "tutti";
  const activeList = filtered.filter((p) => p.status === "attivo" || p.status === "in_pausa");
  const archiveList = filtered.filter((p) => p.status === "concluso");

  return (
    <div data-testid="progetti-page">
      <section className="bg-[#FFF1E8] py-16 lg:py-20">
        <div className="container-vv">
          <div className="eyebrow">Progetti</div>
          <h1 className="mt-4 font-display font-medium text-5xl sm:text-6xl tracking-tight text-[#292522]">Dall'intreccio nascono i nostri progetti</h1>
          <p className="mt-4 text-lg text-[#292522]/75 max-w-3xl">I nostri progetti sono l'intreccio dei tre fili del nostro approccio. Qui trovi quelli attivi oggi e l'archivio storico dei progetti conclusi.</p>
        </div>
      </section>

      <section className="container-vv py-8 sticky top-20 z-30 bg-[#FFFCF8]/95 backdrop-blur-md">
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filtro stato progetti">
            {STATUS_TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  role="tab"
                  aria-selected={active}
                  onClick={() => setTab(t.key)}
                  data-testid={`tab-${t.key}`}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold border transition-colors ${active ? "bg-[#FF6100] text-white border-[#FF6100]" : "bg-white text-[#292522] border-[#292522]/10 hover:border-[#FF6100]"}`}
                >
                  {Icon && <Icon className="h-4 w-4" strokeWidth={1.75} />}
                  {t.label}
                  <span className={`ml-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${active ? "bg-white/25 text-white" : "bg-[#FFF1E8] text-[#FF6100]"}`}>{counts[t.key]}</span>
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-3 items-center">
            <label className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#292522]/40" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Cerca un progetto…"
                className="w-full pl-11 pr-4 py-3 rounded-full bg-white border border-[#292522]/10 focus:border-[#FF6100] focus:ring-2 focus:ring-[#FF6100]/20 outline-none text-sm"
                data-testid="projects-search-input"
              />
            </label>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setArea("")} className={`px-4 py-2 rounded-full text-xs font-semibold border ${!area ? "bg-[#216B65] text-white border-[#216B65]" : "bg-white text-[#292522] border-[#292522]/10 hover:border-[#216B65]"}`}>Tutte le aree</button>
              {AREAS.map((a) => (
                <button key={a.key} onClick={() => setArea(a.key)} data-testid={`filter-area-${a.key}`} className={`px-4 py-2 rounded-full text-xs font-semibold border ${area === a.key ? "bg-[#216B65] text-white border-[#216B65]" : "bg-white text-[#292522] border-[#292522]/10 hover:border-[#216B65]"}`}>{a.short}</button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="container-vv pb-24">
        {filtered.length === 0 ? (
          <div className="rounded-3xl bg-[#FFF1E8] p-12 text-center text-[#292522]/70">
            {tab === "conclusi" ? "Non ci sono ancora progetti conclusi da archiviare." : tab === "in_pausa" ? "Nessun progetto in pausa al momento." : "Nessun progetto trovato con questi filtri."}
          </div>
        ) : showActiveThenArchive ? (
          <div className="space-y-14">
            {activeList.length > 0 && (
              <div>
                <div className="flex items-baseline gap-3 mb-6">
                  <Sparkles className="h-5 w-5 text-[#FF6100]" strokeWidth={1.75} />
                  <h2 className="font-display text-3xl font-medium text-[#292522]">Progetti attivi</h2>
                  <span className="text-sm text-[#292522]/50">{activeList.length}</span>
                </div>
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {activeList.map((p, i) => <ProjectCard key={p.id} project={p} testId={`project-card-active-${i}`} />)}
                </div>
              </div>
            )}
            {archiveList.length > 0 && (
              <div>
                <div className="rounded-3xl bg-[#F8F1E8] p-8 mb-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Archive className="h-5 w-5 text-[#292522]" strokeWidth={1.75} />
                    <h2 className="font-display text-3xl font-medium text-[#292522]">Archivio — Progetti conclusi</h2>
                    <span className="text-sm text-[#292522]/50">{archiveList.length}</span>
                  </div>
                  <p className="text-[#292522]/75 leading-relaxed max-w-3xl">
                    I progetti conclusi continuano a raccontare chi siamo. In questo archivio conserviamo la memoria delle attività realizzate, delle persone incontrate e delle realtà con cui abbiamo collaborato. Ogni esperienza ha contribuito a costruire la storia e la rete di Vola Vola.
                  </p>
                </div>
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {archiveList.map((p, i) => <ProjectCard key={p.id} project={p} testId={`project-card-archive-${i}`} />)}
                </div>
              </div>
            )}
          </div>
        ) : tab === "conclusi" ? (
          <div>
            <div className="rounded-3xl bg-[#F8F1E8] p-8 mb-6">
              <div className="flex items-center gap-3 mb-2">
                <Archive className="h-5 w-5 text-[#292522]" strokeWidth={1.75} />
                <h2 className="font-display text-3xl font-medium text-[#292522]">Il nostro archivio</h2>
              </div>
              <p className="text-[#292522]/75 leading-relaxed max-w-3xl">
                I progetti conclusi continuano a raccontare chi siamo. In questo archivio conserviamo la memoria delle attività realizzate, delle persone incontrate e delle realtà con cui abbiamo collaborato. Ogni esperienza ha contribuito a costruire la storia e la rete di Vola Vola.
              </p>
            </div>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filtered.map((p, i) => <ProjectCard key={p.id} project={p} testId={`project-card-${i}`} />)}
            </div>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((p, i) => <ProjectCard key={p.id} project={p} testId={`project-card-${i}`} />)}
          </div>
        )}
      </section>
    </div>
  );
}
